import moment from "moment";
import NumeroALetras from "../../../../componentes/funciones/NumeroALetras";
import { generarPDF } from "../../../../componentes/imprimir/GenerarPDF";
import { generarImagenes } from "../../../../componentes/imprimir/GenerarImagenes";
import { ImprimirEnIframe } from "../../../../componentes/imprimir/ImprimirEnIframe";
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda, formatoNumero } from "../../../../configuracion/validaciones";
import { ImprimirEnIframeCarta } from "../../../../componentes/imprimir/ImprimirEnIframeCarta";

export const ImprimirVenta = (venta, formato = 1, descargarImagen = false) => {
  if (!venta) return;

  // Desempaquetar en caso de que venta venga como un arreglo [venta] o envuelta en { data }
  const ventaData = Array.isArray(venta) ? (venta[0] || {}) : (venta?.data || venta || {});

  const {
    numeroFactura,
    Cliente,
    Empleado,
    ventadetallepedidos = [],
    ventadetallecita = [],
    descuento = 0,
    descuentoTercera = 0,
    exento = 0,
    exonerado = 0,
    importeImpuesto15 = 0,
    importeImpuesto18 = 0,
    impuesto15 = 0,
    impuesto18 = 0,
    totalVenta = 0,
    subtotal = 0,
    pagoVenta = [],
    rtn,
    facturas = [],
    recibos = [],
    creditos = [],
    createdAt
  } = ventaData;

  // Recuperar ítems con soporte de nombres alternativos del payload
  const rawItems = (ventadetallepedidos && ventadetallepedidos.length > 0)
    ? ventadetallepedidos
    : (ventaData.ventaDetallePedidos || ventaData.items || ventaData.detalles || []);
  const rawCitas = ventadetallecita || ventaData.ventaDetalleCita || [];
  const items = [...rawItems, ...rawCitas];

  const clienteData = Cliente || ventaData.cliente || {};
  const empleadoData = Empleado || ventaData.empleado || {};
  const clienteNombreCompleto = clienteData?.nombreCorto || clienteData?.nombre || 'Consumidor Final';
  const empresaNombre = clienteData?.ClienteEmpresas?.[0]?.nombre;
  const empresaRtn = clienteData?.ClienteEmpresas?.[0]?.rtn;

  const rtnAImprimir =
    rtn ||
    clienteData?.rtn ||
    empresaRtn || "";

  let nombreAImprimir = clienteNombreCompleto;

  if (rtnAImprimir && rtnAImprimir === empresaRtn && empresaNombre && empresaNombre !== "") {
    nombreAImprimir = empresaNombre;
  }

  const pagosCompletados = (pagoVenta && pagoVenta.length > 0)
    ? pagoVenta.map(pv => pv.pago || pv).filter(p => p && p.estado !== 'Anulada')
    : (ventaData.pago ? [ventaData.pago] : (ventaData.pagos || []));
  const pago = pagosCompletados[pagosCompletados.length - 1] || ventaData.pago || {};
  const fechaHora = moment(createdAt || ventaData.fecha || new Date()).format("DD-MM-YYYY HH:mm:ss");

  let efectivo = pago.pagoefectivos?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;
  if (!efectivo && pago.efectivo) {
    if (Array.isArray(pago.efectivo)) {
      efectivo = pago.efectivo.reduce((sum, p) => sum + parseFloat(p.monto || p || 0), 0);
    } else if (!isNaN(parseFloat(pago.efectivo))) {
      efectivo = parseFloat(pago.efectivo);
    }
  }

  let transferencia = pago.pagotransferencia?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;
  if (!transferencia && pago.transferencia) {
    if (Array.isArray(pago.transferencia)) {
      transferencia = pago.transferencia.reduce((sum, p) => sum + parseFloat(p.monto || p || 0), 0);
    } else if (!isNaN(parseFloat(pago.transferencia))) {
      transferencia = parseFloat(pago.transferencia);
    }
  }

  let tarjeta = pago.pagotarjeta?.reduce((sum, p) => sum + parseFloat(p.monto || 0), 0) || 0;
  if (!tarjeta && pago.tarjeta) {
    if (Array.isArray(pago.tarjeta)) {
      tarjeta = pago.tarjeta.reduce((sum, p) => sum + parseFloat(p.monto || p || 0), 0);
    } else if (!isNaN(parseFloat(pago.tarjeta))) {
      tarjeta = parseFloat(pago.tarjeta);
    }
  }

  const factura = Array.isArray(facturas) && facturas.length > 0 ? facturas[0] : (ventaData.factura || null);
  const facturacai = factura?.facturacai ?? null;
  const facturaNumero = factura?.numeroFactura ?? 0;
  const CAI = facturacai?.CAI ?? null;

  const recibo = Array.isArray(recibos) && recibos.length > 0 ? recibos[0] : (ventaData.recibo || null);
  const reciboNumero = recibo?.id ?? ventaData.reciboId ?? 0;

  const isFacturaDoc = Boolean(factura || (Array.isArray(facturas) && facturas.length > 0));
  const isReciboDoc = Boolean(recibo || (Array.isArray(recibos) && recibos.length > 0));
  const isCreditoDoc = Boolean((Array.isArray(creditos) && creditos.length > 0) || ventaData.credito);

  const reciboId = isReciboDoc ? (recibo?.id || ventaData.reciboId || null) : null;
  const creditoId = isCreditoDoc ? (creditos[0]?.id || ventaData.creditoId || null) : null;

  const tipoDocumento = isFacturaDoc
    ? "FACTURA"
    : isCreditoDoc
      ? "CREDITO"
      : "RECIBO";

  const numeroDocumento = isFacturaDoc
    ? "000-002-01-" + formatoNumero(facturaNumero)
    : isReciboDoc ?
      String(reciboNumero || "—") :
      "—";

  const numeroInicio = isFacturaDoc
    ? "000-002-01-" + formatoNumero(facturacai?.numeroInicial)
    : String(ventaData?.id || "—");

  const numeroFin = isFacturaDoc
    ? "000-002-01-" + formatoNumero(facturacai?.numeroFinal)
    : String(ventaData?.id || "—");

  const constanciaExonerado = facturas[0]?.facturaconstancia?.[0]?.numeroConstancia || null;
  const ordenCompraExenta = facturas[0]?.facturaexentos?.[0]?.numeroOrden || null;
  const registroSag = facturas[0]?.facturasags?.[0]?.numeroRegistro || null;

  const descuentoGlobal = parseFloat(descuento);

  // Robustez de cálculo: respaldo por si el servidor o la red no enviaron subtotales o total completo
  const subtotalItemsCalculado = items.reduce((sum, item) => {
    const sub = parseFloat(item.subtotal || (parseFloat(item.precio || item.precioSinImpuesto || 0) * parseFloat(item.cantidad || 0)) || 0);
    return sum + sub;
  }, 0);

  const subtotalFinal = (parseFloat(subtotal) > 0) ? parseFloat(subtotal) : subtotalItemsCalculado;
  const totalVentaFinal = (parseFloat(totalVenta) > 0)
    ? parseFloat(totalVenta)
    : Math.max(0, subtotalFinal - parseFloat(descuento || 0) + parseFloat(impuesto15 || 0) + parseFloat(impuesto18 || 0));

  let recibido = parseFloat(pago.recibido || 0);
  if (recibido === 0 && !isCreditoDoc) {
    const sumaPagos = efectivo + transferencia + tarjeta;
    recibido = sumaPagos > 0 ? sumaPagos : totalVentaFinal;
  }
  const cambio = Math.max(recibido - totalVentaFinal, 0);

  const reciboSubtotal = parseFloat(subtotalFinal + impuesto15 + importeImpuesto18 + descuento);

  const fechaLimiteFormateada =
    facturacai?.fechaLimite && moment(facturacai.fechaLimite).isValid()
      ? moment(facturacai.fechaLimite).format("DD/MM/YYYY")
      : null;

  let html = `
      <div class="ticket-impresion" style="width:280px; font-family:'Arial', sans-serif; font-size:14px; color:#000000 !important; background-color:#ffffff; padding:0; margin:0;">
        <style>
            .ticket-impresion, .ticket-impresion * {
                color: #000000 !important;
                -webkit-text-fill-color: #000000 !important;
                box-sizing: border-box;
            }
            .ticket-impresion strong, .ticket-impresion b, .ticket-impresion .font-weight-bold {
                color: #000000 !important;
                -webkit-text-fill-color: #000000 !important;
                font-weight: bold !important;
            }
        </style>
          <img src=${NegocioConfig.logo} style="width:150px; height:100px; display:block; margin:0 auto; padding:0; border:0;" alt="Logo" />
        <div style="text-align:center; margin-bottom:5px;">
            <div style="font-weight:bold; font-size:16px; color:#000000 !important;">${NegocioConfig.nombre}</div>
          <div style="font-size: 12px; color:#000000 !important;">
            RTN: ${NegocioConfig.rtn}
          </div>
          <div style="font-size: 12px; color:#000000 !important;">
            ${NegocioConfig.direccion}
          </div>
          <div style="font-size: 12px; color:#000000 !important;">
            Tel: ${NegocioConfig.telefono}
          </div>
        <div style="font-weight:bold; margin:5px 0; text-align: left; color:#000000 !important;">${tipoDocumento} No. ${numeroDocumento}</div>
         ${isReciboDoc && reciboId ? `<div style="text-align: left; font-size: 12px; color:#000000 !important;">Recibo No: ${reciboId}</div>` : ""}
        ${isCreditoDoc && creditoId ? `<div style="text-align: left; font-size: 12px; color:#000000 !important;">Crédito No: ${creditoId}</div>` : ""}
        ${isFacturaDoc
      ? `
            <div style="text-align: left; color:#000000 !important;">CAI:</div>
            <div style="text-align: left; color:#000000 !important;">${CAI}</div>
          `
      : ""
    }
        <div style="text-align: left; color:#000000 !important;"><span>Fecha:</span> ${fechaHora}</div>
        <div style="text-align: left; color:#000000 !important;"><span>Atendió:</span> ${empleadoData?.nombreCorto || empleadoData?.nombre || "Atendió"}</div>
      </div>
      <hr style="border:1px dashed black;" />
        <div style="color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">Cliente:</strong> ${nombreAImprimir}</div>
        <div style="color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">RTN:</strong> ${rtnAImprimir || 'N/A'}</div> 
        ${isFacturaDoc
      ? `
            <div style="font-size:12px; margin-top:5px; padding-top:5px; color:#000000 !important;">
                <div style="font-weight:bold; font-size:13px; text-decoration:underline; margin-bottom:2px; color:#000000 !important;">CERTIFICADOS</div>
                <div>Nº Constancia de Exonerado: ${constanciaExonerado || 'N/A'}</div>
                <div>Nº Orden de Compra Exenta: ${ordenCompraExenta || 'N/A'}</div>
                <div>Nº Registro de la SAG: ${registroSag || 'N/A'}</div>
            </div>
            `
      : ''
    }
        <hr style="border:1px dashed black;" />
        <div style="margin-top:5px;">
          ${items.map((item) => {
      const nombre = item.producto?.nombre || item.servicio?.nombre || item.nombre || "—";
      const codigo = item.producto?.codigo || item.servicio?.codigo || item.codigo || "—";
      const cantidad = parseFloat(item.cantidad || 0);
      const precioUnitario = isFacturaDoc ? parseFloat(item.precioSinImpuesto || item.precio || 0) : parseFloat(item.precio || 0); // Precio final de venta
      const precioOriginal = parseFloat(item.precioOriginal || precioUnitario); // Precio de lista
      const descuentoItem = parseFloat(item.descuento || 0); // Descuento por ítem
      const subtotalMostrado = isFacturaDoc ? parseFloat(item.subtotalSinImpuesto || (precioUnitario * cantidad) || 0) : parseFloat(item.subtotal || (precioUnitario * cantidad) || 0);
      const mostrarDescuento = descuentoItem > 0 && precioOriginal > precioUnitario;

      return `
              <div style="margin-bottom:5px; color:#000000 !important;">
                <div style="font-size:11px; margin-bottom:2px; color:#000000 !important;">
                  ${codigo}
                </div>
                <div style="font-weight:bold; font-size:13px; color:#000000 !important;">${nombre}</div>
                ${mostrarDescuento ?
          `<div style="font-size:11px; color:red !important; line-height:1.2;">
                      <span>DES: ${formatoMoneda(descuentoItem)}</span>
                    </div>`
          : ""}
                  
                <div style="display:flex; justify-content:space-between; font-size:12px; color:#000000 !important;">
                  <span>${cantidad} x ${formatoMoneda(precioUnitario)}</span>
                  <span style="font-weight:bold; color:#000000 !important;">${formatoMoneda(subtotalMostrado)}</span>
                </div>
              </div>`;
    }).join("")}
        </div>
        <hr style="border:1px dashed black;" />
        <div style="text-align:right; color:#000000 !important;">
          <div>Subtotal Ítems: <strong style="color:#000000 !important; font-weight:bold;">${isFacturaDoc ? formatoMoneda(subtotalFinal) : formatoMoneda(reciboSubtotal)}</strong></div>

          ${descuentoGlobal > 0 ?
      `<div>Descuento / rebajas otorgadas: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(descuentoGlobal)}</strong></div>`
      : ""}

          ${!isFacturaDoc
      ? `
              <div>Total a Pagar: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(totalVentaFinal)}</strong></div>
              <div>Total Recibido: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(recibido)}</strong></div>
              ${efectivo > 0 ? `<div>Efectivo: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(efectivo)}</strong></div>` : ""}
              ${tarjeta > 0 ? `<div>Tarjeta: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(tarjeta)}</strong></div>` : ""}
              ${transferencia > 0 ? `<div>Transferencia: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(transferencia)}</strong></div>` : ""}
              <div style="font-size:14px; font-weight:bold; color:#000000 !important;">Cambio: ${formatoMoneda(cambio)}</div>
            `
      : `
              <div>Importe Exonerado: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(exonerado)}</strong></div>
              <div>Importe Exento: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(exento)}</strong></div>
              <div>Importe 15%: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(importeImpuesto15)}</strong></div>
              <div>ISV 15%: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(impuesto15)}</strong></div>
              <div>Importe 18%: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(importeImpuesto18)}</strong></div>
              <div>ISV 18%: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(impuesto18)}</strong></div>
              
              <div style="font-size:14px; font-weight:bold; color:#000000 !important;">Total: ${formatoMoneda(totalVentaFinal)}</div>
              <div style="margin-top:4px; margin-bottom:8px; font-weight:bold; font-size:12px; text-transform:uppercase; color:#000000 !important;">
                ${NumeroALetras(totalVentaFinal)}
              </div>
              <div>Total Recibido: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(recibido)}</strong></div>
              ${efectivo > 0 ? `<div>Efectivo: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(efectivo)}</strong></div>` : ""}
              ${tarjeta > 0 ? `<div>Tarjeta: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(tarjeta)}</strong></div>` : ""}
              ${transferencia > 0 ? `<div>Transferencia: <strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(transferencia)}</strong></div>` : ""}
              <div style="font-size:14px; font-weight:bold; color:#000000 !important;">Cambio: ${formatoMoneda(cambio)}</div>
            `
    }
      </div>
      <div style="text-align:center; margin-top:20px; font-size:14px; color:#000000 !important;">
        ${pago.documento === "FA" || isFacturaDoc
      ? ""
      : `<strong style="color:#000000 !important; font-weight:bold;">¡Gracias por su compra!</strong>`}
        ${isFacturaDoc
      ? `
          <div style="margin-top: 10px; color:#000000 !important;">
            <div>Rango Autorizado:</div>
            <div>${numeroInicio} al ${numeroFin} </div>
            <div>Fecha limite de emisión: ${fechaLimiteFormateada} </div>
          </div>
          `
      : ""
    }
      </div>
    </div>
    `;
  if (formato == 2)
    html = `
    <div class="ticket-impresion" style="width:800px; max-width: 100%; font-family:'Arial', sans-serif; font-size:12px; color:#000000 !important; background-color:#ffffff; padding:20px; margin:0 auto; border: 1px solid #ccc;">
        <style>
            .ticket-impresion, .ticket-impresion * {
                color: #000000 !important;
                -webkit-text-fill-color: #000000 !important;
                box-sizing: border-box;
            }
            .ticket-impresion strong, .ticket-impresion b, .ticket-impresion .font-weight-bold, .ticket-impresion th {
                color: #000000 !important;
                -webkit-text-fill-color: #000000 !important;
                font-weight: bold !important;
            }
        </style>
        <img src=${NegocioConfig.logo} style="width:150px; height:100px; display:block; margin:0 auto; padding:0; border:0;" alt="Logo" />
        <div style="text-align:center; margin-bottom:15px; color:#000000 !important;">
            <div style="font-weight:bold; font-size:24px; margin-bottom: 5px; color:#000000 !important;">${NegocioConfig.nombre}</div>
            <div style="font-size: 14px; margin-bottom: 3px; color:#000000 !important;">
                ${NegocioConfig.rtn}
            </div>
            <div style="font-size: 14px; margin-bottom: 3px; color:#000000 !important;">
                ${NegocioConfig.direccion}
            </div>
            <div style="font-size: 14px; color:#000000 !important;">
                Tel: ${NegocioConfig.telefono}
            </div>
        </div>

        <div style="border-bottom: 2px solid black; margin-bottom: 15px;"></div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 20px; color:#000000 !important;">
            <div style="width: 45%; font-size: 14px; color:#000000 !important;">
                <div style="font-weight:bold; font-size:18px; margin-bottom: 8px; color:#000000 !important;">${tipoDocumento} No. ${numeroDocumento} </div>
                ${isFacturaDoc
        ? `
            <div style="margin-bottom: 3px; color:#000000 !important;">CAI: ${CAI}</div>
          `
        : ""
      }
                <div style="margin-bottom: 3px; color:#000000 !important;">${fechaHora}</div>
                <div style="margin-bottom: 3px; color:#000000 !important;">Atendió: ${empleadoData?.nombreCorto || empleadoData?.nombre || "Atendió"}</div>
            </div>
            <div style="width: 45%; font-size: 14px; color:#000000 !important;">
                <div style="font-weight:bold; font-size:16px; margin-bottom: 5px; color:#000000 !important;">INFORMACIÓN DEL CLIENTE</div>
                <div style="margin-bottom: 3px; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">Nombre:</strong> ${nombreAImprimir}</div>
                <div style="margin-bottom: 3px; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">RTN Cliente:</strong> ${rtnAImprimir || 'N/A'}</div>
                 ${isFacturaDoc
        ? `
            <div style="margin-top:10px; color:#000000 !important;">
                    <div style="font-weight:bold; font-size:14px; text-decoration:underline; margin-bottom:2px; color:#000000 !important;">CERTIFICADOS</div>
                    <div>Nº Constancia de Exonerado: ${constanciaExonerado || 'N/A'}</div>
                    <div>Nº Orden de Compra Exenta: ${ordenCompraExenta || 'N/A'}</div>
                    <div>Nº Registro de la SAG: ${registroSag || 'N/A'}</div>
                </div>
            `
        : ''
      }
                
            </div>
        </div>

        <div style="border-bottom: 1px solid black; margin-bottom: 15px;"></div>

        <div style="font-weight:bold; font-size:16px; margin-bottom:10px; color:#000000 !important;">DETALLE DE PRODUCTOS</div>
        
        <table style="width:100%; font-size:14px; border-collapse: collapse; margin-bottom:20px; border: 1px solid black; color:#000000 !important;">
            <thead style="background-color: #f0f0f0;">
                <tr style="border-bottom: 2px solid black;">
                    <th style="text-align: left; padding: 5px 10px; width: 35%; color:#000000 !important; font-weight:bold;">Descripción</th>
                    <th style="text-align: center; padding: 5px 10px; width: 15%; color:#000000 !important; font-weight:bold;">Cant.</th>
                    <th style="text-align: right; padding: 5px 10px; width: 15%; color:#000000 !important; font-weight:bold;">P. Unit.</th>
                    <th style="text-align: center; padding: 5px 10px; width: 15%; color:#000000 !important; font-weight:bold;">Des.</th>
                    <th style="text-align: right; padding: 5px 10px; width: 20%; color:#000000 !important; font-weight:bold;">Subtotal</th>
                </tr>
            </thead>
            <tbody>
                ${items.map((item) => {
        const nombre = item.producto?.nombre || item.servicio?.nombre || item.nombre || "—";
        const codigo = item.producto?.codigo || item.servicio?.codigo || item.codigo || "—";
        const cantidad = parseFloat(item.cantidad || 0);
        const precioUnitario = parseFloat(item.precioSinImpuesto || item.precio || 0); // Precio final de venta
        const precioOriginal = parseFloat(item.precioOriginal || precioUnitario); // Precio de lista
        const descuentoItem = parseFloat(item.descuento || 0); // Descuento por ítem
        const subtotalMostrado = parseFloat(item.subtotalSinImpuesto || (precioUnitario * cantidad) || 0); // Subtotal basado en el precio final de venta
        const mostrarDescuento = descuentoItem > 0 && precioOriginal > precioUnitario;

        return `
                <tr style="border-bottom: 1px dashed #ccc; color:#000000 !important;">
                    <td style="text-align: left; padding: 5px 10px; color:#000000 !important;">${codigo} ${nombre}</td>
                    <td style="text-align: center; padding: 5px 10px; color:#000000 !important;">${cantidad}</td>
                    <td style="text-align: right; padding: 5px 10px; color:#000000 !important;">${formatoMoneda(precioUnitario)}</td>
                    <td style="text-align: right; padding: 5px 10px; color:#000000 !important;">${formatoMoneda(descuentoItem)}</td>
                    <td style="text-align: right; padding: 5px 10px; color:#000000 !important;">${formatoMoneda(subtotalMostrado)}</td>
                </tr>
             `;
      }).join("")}  
            </tbody>
        </table>

        <div style="display: flex; justify-content: flex-end; color:#000000 !important;">
            <div style="width: 100%;">
                <table style="width:100%; font-size:14px; border-collapse: collapse; margin-bottom: 15px; color:#000000 !important;">
                    <tr>
                        <td style="text-align: left; padding: 2px 0; width: 70%; color:#000000 !important;">Subtotal items:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(subtotalFinal)}</strong></td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Descuento:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(descuentoGlobal)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Importe Exonerado:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(exonerado)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Importe Exento:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(exento)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Importe 15%:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(importeImpuesto15)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Importe 18%:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(importeImpuesto18)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">ISV 15:%</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(impuesto15)}</td>
                    </tr>
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">ISV 18:%</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;">${formatoMoneda(impuesto18)}</td>
                    </tr>
                    <tr style="font-size:16px; font-weight:bold; border-top: 1px solid black;">
                        <td style="text-align: left; padding: 5px 0; color:#000000 !important;">TOTAL:</td>
                        <td style="text-align: right; padding: 5px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(totalVentaFinal)}</strong></td>
                    </tr>
                </table>

                <div style="font-weight:bold; font-size:14px; text-transform:uppercase; text-align:center; margin-bottom: 20px; padding: 5px; border: 1px solid #ccc; color:#000000 !important;">
                    ${NumeroALetras(totalVentaFinal)}
                </div>
                
                <div style="font-weight:bold; font-size:16px; margin-bottom:5px; color:#000000 !important;">DETALLE DE PAGO</div>
                <table style="width:100%; font-size:14px; border-collapse: collapse; color:#000000 !important;">
                    <tr>
                        <td style="text-align: left; padding: 2px 0; width: 70%; color:#000000 !important;">Total Recibido: ${formatoMoneda(recibido)}</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;"></td>
                    </tr>
                    ${efectivo > 0 ? `
                    <tr>
                        <td style="text-align: left; padding: 2px 0; color:#000000 !important;">Efectivo:</td>
                        <td style="text-align: right; padding: 2px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(efectivo)}</strong></td>
                    </tr>
                    ` : ""}
                    ${tarjeta > 0 ? `
                    <tr style="font-size:16px; font-weight:bold; border-top: 1px solid black;">
                        <td style="text-align: left; padding: 5px 0; color:#000000 !important;">Tarjeta:</td>
                        <td style="text-align: right; padding: 5px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(tarjeta)}</strong></td>
                    </tr>
                    ` : ""}
                    ${transferencia > 0 ? `
                    <tr style="font-size:16px; font-weight:bold; border-top: 1px solid black;">
                        <td style="text-align: left; padding: 5px 0; color:#000000 !important;">Transferencia:</td>
                        <td style="text-align: right; padding: 5px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(transferencia)}</strong></td>
                    </tr>
                    ` : ""}
                    <tr style="font-size:16px; font-weight:bold; border-top: 1px solid black;">
                        <td style="text-align: left; padding: 5px 0; color:#000000 !important;">Cambio:</td>
                        <td style="text-align: right; padding: 5px 0; color:#000000 !important;"><strong style="color:#000000 !important; font-weight:bold;">${formatoMoneda(cambio)}</strong></td>
                    </tr>
                </table>
            </div>
        </div>
        
        <div style="border-bottom: 1px solid black; margin-top: 20px; margin-bottom: 15px;"></div>

        <div style="text-align:center; font-size:14px; color:#000000 !important;">
            <strong style="display: block; margin-bottom: 15px; color:#000000 !important; font-weight:bold;">¡Gracias por su compra!</strong>
             ${isCreditoDoc ? `
              <div style="margin-top: 50px; border-top: 1px solid black; width: 80%; margin-left: 10%; text-align: center; color:#000000 !important;">
                <div style="font-size: 12px; margin-top: 5px; color:#000000 !important;">Firma del Cliente</div>
              </div>
            ` : ""}
            ${isFacturaDoc
        ? `
            <div style="font-size:12px; color:#000000 !important;">
                <div>Rango Autorizado: ${numeroInicio} al ${numeroFin}</div>
                <div>Fecha limite de emisión: ${fechaLimiteFormateada}</div>
            </div>
            `
        : ""
      }
            <div style="margin-top: 5px; font-size:12px; color:#000000 !important;">
                Documento diseñado por DESOFIW
            </div>
        </div>
    </div>
`;

  ImprimirEnIframe(html, `Comprobante de Venta ${numeroDocumento}`, '5px');
  if (descargarImagen) {
    setTimeout(() => {
      generarImagenes(html, 'Comprobante_Venta', numeroDocumento, 70);
    }, 1000);
  }
  //const nombrePDF = `Comprobante_Venta_${numeroDocumento}.pdf`;
  //generarPDF(html, nombrePDF, 70);
};
